import React, { useState, useEffect } from 'react';
import AdminNavigation from '../../components/ui/AdminNavigation';
import NotificationPanel from '../../components/ui/NotificationPanel';
import MetricCard from './components/MetricCard';
import UserManagementTable from './components/UserManagementTable';
import InstituteApprovalCard from './components/InstituteApprovalCard';
import AnalyticsChart from './components/AnalyticsChart';
import EventConfigPanel from './components/EventConfigPanel';
import ActivityMonitor from './components/ActivityMonitor';
import ReportGenerator from './components/ReportGenerator';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import AddInstituteModal from './components/AddInstituteModal';
import EditUserModal from './components/EditUserModal';
import BulkStudentUploadModal from './components/BulkStudentUploadModal';
import ViewUserDetailsModal from './components/ViewUserDetailsModal';
import ViewInstituteDetailsModal from './components/ViewInstituteDetailsModal';
import ViewCredentialsModal from './components/ViewCredentialsModal';
import EditInstituteModal from './components/EditInstituteModal';
import { instituteService } from '../../services/instituteService';
import { adminService, exportDataToCSV, getAdminMetrics, getRecentActivities, getRegistrationTrend, getEngagementData, getBoothVisitsTrend } from '../../services/adminService';
import EventRemindersPanel from './components/EventRemindersPanel';
import { useAuth } from '../../contexts/AuthContext';
import AnnouncementManager from './components/AnnouncementManager';
import BoothImageManager from './components/BoothImageManager';
import BoothManagementPanel from './components/BoothManagementPanel';
import StudentApprovalPanel from './components/StudentApprovalPanel';

import WebinarManagementPanel from './components/WebinarManagementPanel';
import BulkInstituteUploadModal from './components/BulkInstituteUploadModal';
import FeaturedInstitutesManager from './components/FeaturedInstitutesManager';
import CategoryEditor from './components/CategoryEditor';
import InstituteConfirmationDialog from './components/InstituteConfirmationDialog';
import ExhibitionHallManager from './components/ExhibitionHallManager';
import CourseLibraryManager from './components/CourseLibraryManager';
import BulkExcelCourseUploadModal from './components/BulkExcelCourseUploadModal';
import BulkCsvCourseUploadModal from './components/BulkCsvCourseUploadModal';
import BulkFaqUploadModal from './components/BulkFaqUploadModal';
import BulkDescriptionUploadModal from './components/BulkDescriptionUploadModal';
import BulkTestimonialUploadModal from './components/BulkTestimonialUploadModal';
import BulkLogoUploadModal from './components/BulkLogoUploadModal';






const AdminDashboard = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [isAddInstituteModalOpen, setIsAddInstituteModalOpen] = useState(false);
  const [isBulkUploadModalOpen, setIsBulkUploadModalOpen] = useState(false);
  const [isBulkInstituteUploadModalOpen, setIsBulkInstituteUploadModalOpen] = useState(false);
  const [isBulkExcelCourseUploadOpen, setIsBulkExcelCourseUploadOpen] = useState(false);
  const [isBulkCsvCourseUploadOpen, setIsBulkCsvCourseUploadOpen] = useState(false);
  const [isBulkFaqUploadOpen, setIsBulkFaqUploadOpen] = useState(false);
  const [isBulkDescriptionUploadOpen, setIsBulkDescriptionUploadOpen] = useState(false);
  const [isBulkTestimonialUploadOpen, setIsBulkTestimonialUploadOpen] = useState(false);
  const [isBulkLogoUploadOpen, setIsBulkLogoUploadOpen] = useState(false);
  const [institutes, setInstitutes] = useState([]);
  const [loadingInstitutes, setLoadingInstitutes] = useState(false);
  const [instituteSearch, setInstituteSearch] = useState('');
  const [notifications, setNotifications] = useState([]);

  // Institute confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, action: null, institute: null });
  const [isConfirmLoading, setIsConfirmLoading] = useState(false);

  const [metrics, setMetrics] = useState([
    {
      title: 'Total Registrations',
      value: '—',
      change: '',
      changeType: 'positive',
      icon: 'Users',
      iconColor: 'var(--color-primary)',
      trend: []
    },
    {
      title: 'Active Users',
      value: '—',
      change: '',
      changeType: 'positive',
      icon: 'Activity',
      iconColor: 'var(--color-success)',
      trend: []
    },
    {
      title: 'Booth Visits',
      value: '—',
      change: '',
      changeType: 'positive',
      icon: 'Store',
      iconColor: 'var(--color-secondary)',
      trend: []
    },
    {
      title: 'Video Sessions',
      value: '—',
      change: '',
      changeType: 'positive',
      icon: 'Video',
      iconColor: 'var(--color-accent)',
      trend: []
    }
  ]);

  const [registrationData, setRegistrationData] = useState([]);
  const [engagementData, setEngagementData] = useState([]);
  const [boothVisitsTrend, setBoothVisitsTrend] = useState([]);
  const [activities, setActivities] = useState([]);

  // Load overview metrics on mount
  useEffect(() => {
    const loadOverviewData = async () => {
      try {
        const [metricsData, activitiesData, trendData, engagementRaw, boothTrend] = await Promise.all([
          getAdminMetrics(),
          getRecentActivities(),
          getRegistrationTrend(),
          getEngagementData(),
          getBoothVisitsTrend()
        ]);

        setMetrics([
          {
            title: 'Total Registrations',
            value: metricsData?.totalRegistrations?.toLocaleString(),
            change: '',
            changeType: 'positive',
            icon: 'Users',
            iconColor: 'var(--color-primary)',
            trend: []
          },
          {
            title: 'Active Students',
            value: metricsData?.activeUsers?.toLocaleString(),
            change: '',
            changeType: 'positive',
            icon: 'Activity',
            iconColor: 'var(--color-success)',
            trend: []
          },
          {
            title: 'Booth Visits',
            value: metricsData?.boothVisits?.toLocaleString(),
            change: '',
            changeType: 'positive',
            icon: 'Store',
            iconColor: 'var(--color-secondary)',
            trend: []
          },
          {
            title: 'Video Sessions',
            value: metricsData?.videoSessions?.toLocaleString(),
            change: '',
            changeType: 'positive',
            icon: 'Video',
            iconColor: 'var(--color-accent)',
            trend: []
          }
        ]);

        setActivities(activitiesData || []);
        setRegistrationData(trendData || []);
        setEngagementData(engagementRaw || []);
        setBoothVisitsTrend(boothTrend || []);
      } catch (err) {
        console.error('Error loading overview data:', err);
      }
    };
    loadOverviewData();
  }, []);

  // Institute account requests state
  const [accountRequests, setAccountRequests] = useState([]);
  const [loadingAccountRequests, setLoadingAccountRequests] = useState(false);

  const tabs = [
  { id: 'overview', label: 'Platform Overview', icon: 'LayoutDashboard' },
  { id: 'users', label: 'User Management', icon: 'Users' },
  { id: 'student-approvals', label: 'Student Approvals', icon: 'UserCheck' },
  { id: 'institutes', label: 'Institute Management', icon: 'Building2' },
  { id: 'account-requests', label: 'Account Requests', icon: 'ClipboardList' },
  { id: 'booths', label: 'Booth Management', icon: 'Store' },
  { id: 'exhibition-hall', label: 'Exhibition Hall', icon: 'DoorOpen' },
  { id: 'featured', label: 'Featured Institutes', icon: 'Star' },
  { id: 'categories', label: 'Category Editor', icon: 'Tag' },
  { id: 'course-library', label: 'Course Library', icon: 'BookOpen' },
  { id: 'booth-images', label: 'Booth Images', icon: 'Image' },
  { id: 'webinars', label: 'Webinar Management', icon: 'Video' },
  { id: 'announcements', label: 'Announcements', icon: 'Megaphone' },
  { id: 'reminders', label: 'Event Reminders', icon: 'Bell' },
  { id: 'analytics', label: 'Analytics', icon: 'BarChart3' },
  { id: 'config', label: 'Event Config', icon: 'Settings' }];


  // User management state
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userError, setUserError] = useState(null);
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // View user details modal state
  const [isViewUserModalOpen, setIsViewUserModalOpen] = useState(false);
  const [viewingUser, setViewingUser] = useState(null);
  
  // View institute details modal state
  const [isViewInstituteModalOpen, setIsViewInstituteModalOpen] = useState(false);
  const [viewingInstitute, setViewingInstitute] = useState(null);

  // Edit institute modal state
  const [isEditInstituteModalOpen, setIsEditInstituteModalOpen] = useState(false);
  const [editingInstitute, setEditingInstitute] = useState(null);

  // View credentials modal state
  const [isViewCredentialsModalOpen, setIsViewCredentialsModalOpen] = useState(false);
  const [credentialsInstitute, setCredentialsInstitute] = useState(null);

  // Delete institute confirmation state
  const [deleteConfirmInstitute, setDeleteConfirmInstitute] = useState(null);
  const [isDeletingInstitute, setIsDeletingInstitute] = useState(false);
  
  // Institute approvals state
  const [pendingInstitutes, setPendingInstitutes] = useState([]);
  const [loadingPendingInstitutes, setLoadingPendingInstitutes] = useState(false);
  const [instituteError, setInstituteError] = useState(null);
  
  // Event config state
  const [eventConfig, setEventConfig] = useState(null);
  const [savingConfig, setSavingConfig] = useState(false);

  const handleMarkAsRead = (notificationId) => {
    setNotifications((prev) =>
    prev?.map((n) => n?.id === notificationId ? { ...n, read: true } : n)
    );
  };

  const handleClearAll = () => {
    setNotifications([]);
  };

  const handleUserAction = async (action, userIds) => {
    try {
      if (action === 'bulk-upload') {
        setIsBulkUploadModalOpen(true);
        return;
      }

      if (action === 'email') {
        // TODO: Implement email functionality
        alert(`Email functionality for ${userIds?.length} user(s) - To be implemented`);
        return;
      }

      if (action === 'suspend') {
        if (!confirm(`Are you sure you want to suspend ${userIds?.length} user(s)?`)) return;
        await adminService?.bulkSuspendUsers(userIds);
        alert('Users suspended successfully');
        fetchUsers();
        return;
      }

      if (action === 'delete') {
        if (!confirm(`Are you sure you want to delete ${userIds?.length} user(s)? This action cannot be undone.`)) return;
        await adminService?.bulkDeleteUsers(userIds);
        alert('Users deleted successfully');
        fetchUsers();
        return;
      }

      if (action === 'view') {
        const userId = userIds?.[0];
        const user = await adminService?.getUserById(userId);
        setViewingUser(user);
        setIsViewUserModalOpen(true);
        return;
      }

      if (action === 'edit') {
        try {
          const userDetails = await adminService?.getUserById(userIds?.[0]);
          setSelectedUser(userDetails);
          setIsEditUserModalOpen(true);
        } catch (error) {
          console.error('Error fetching user details:', error);
          alert(error?.message || 'Failed to fetch user details');
        }
        return;
      }
    } catch (error) {
      console.error('Error handling user action:', error);
      alert(error?.message || 'Failed to perform action');
    }
  };

  const handleApproveInstitute = async (institute) => {
    setConfirmDialog({ isOpen: true, action: 'approve', institute });
  };

  const handleRejectInstitute = async (institute) => {
    setConfirmDialog({ isOpen: true, action: 'reject', institute });
  };

  const handleConfirmAction = async () => {
    const { action, institute } = confirmDialog;
    if (!institute) return;
    setIsConfirmLoading(true);
    try {
      if (action === 'approve') {
        await adminService?.approveInstitute(institute?.id);
        fetchPendingInstitutes();
        fetchAllInstitutes();
      } else {
        await adminService?.rejectInstitute(institute?.id);
        fetchPendingInstitutes();
      }
      setConfirmDialog({ isOpen: false, action: null, institute: null });
    } catch (error) {
      console.error(`Error ${action}ing institute:`, error);
      alert(error?.message || `Failed to ${action} institute`);
    } finally {
      setIsConfirmLoading(false);
    }
  };

  const handleCancelConfirm = () => {
    if (!isConfirmLoading) {
      setConfirmDialog({ isOpen: false, action: null, institute: null });
    }
  };

  const handleViewInstituteDetails = async (instituteId) => {
    try {
      const details = await adminService?.getInstituteDetails(instituteId);
      setViewingInstitute(details);
      setIsViewInstituteModalOpen(true);
    } catch (error) {
      console.error('Error fetching institute details:', error);
      alert(error?.message || 'Failed to fetch institute details');
    }
  };

  const handleEditInstitute = (institute) => {
    setEditingInstitute(institute);
    setIsEditInstituteModalOpen(true);
  };

  const handleSaveInstituteProfile = async (instituteId, profileData) => {
    await adminService?.updateInstituteProfile(instituteId, profileData);
    await loadInstitutes();
  };

  const handleSaveConfig = (config) => {
    console.log('Save config:', config);
  };

  const handleGenerateReport = async (config) => {
    try {
      await exportDataToCSV(config);
    } catch (error) {
      console.error('Error generating report:', error);
      alert(error?.message || 'Failed to generate report');
    }
  };

  const handleAddInstitute = async (formData) => {
    try {
      const result = await instituteService?.createInstitute(formData);
      await loadInstitutes();
      if (result?.recovered) {
        // Orphaned auth account was recovered — show informational message
        alert(`Institute account for "${formData?.instituteName}" was recovered successfully. The auth account already existed but had no institute profile — the profile has now been created and the institute will appear in the list.`);
      }
    } catch (error) {
      console.error('Error adding institute:', error);
      throw error;
    }
  };

  const handleToggleActivation = async (instituteId, type, currentValue) => {
    try {
      const updatePayload =
        type === 'chat'
          ? { chatEnabled: !currentValue }
          : type === 'video'
          ? { videoEnabled: !currentValue }
          : { tour360Enabled: !currentValue };

      await instituteService?.updateInstituteActivation(instituteId, updatePayload);

      setInstitutes(prev => prev?.map(inst =>
        inst?.id === instituteId
          ? {
              ...inst,
              ...(type === 'chat'
                ? { chatEnabled: !currentValue }
                : type === 'video'
                ? { videoEnabled: !currentValue }
                : { tour360Enabled: !currentValue })
            }
          : inst
      ));
    } catch (error) {
      console.error('Error toggling activation:', error);
      alert(error?.message || 'Failed to update activation status');
    }
  };

  const handleUpdateUser = async (userId, userData) => {
    try {
      await adminService?.updateUser(userId, userData);
      await fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  };

  const loadInstitutes = async () => {
    setLoadingInstitutes(true);
    try {
      const data = await instituteService?.getAllInstitutesForAdmin();
      setInstitutes(data || []);
    } catch (error) {
      console.error('Error loading institutes:', error);
    } finally {
      setLoadingInstitutes(false);
    }
  };

  // Load institutes when institutes tab is active
  React.useEffect(() => {
    if (activeTab === 'institutes') {
      loadInstitutes();
    }
  }, [activeTab]);

  // Fetch users on component mount or when users tab is active
  useEffect(() => {
    if (activeTab === 'users') {
      fetchUsers();
    }
  }, [activeTab]);

  // Fetch pending institutes when institutes tab is active
  useEffect(() => {
    if (activeTab === 'institutes') {
      fetchPendingInstitutes();
      fetchAllInstitutes();
    }
  }, [activeTab]);

  // Fetch event config when config tab is active
  useEffect(() => {
    if (activeTab === 'config') {
      fetchEventConfig();
    }
  }, [activeTab]);

  // Load account requests when tab is active
  React.useEffect(() => {
    if (activeTab === 'account-requests') {
      fetchAccountRequests();
    }
  }, [activeTab]);

  const fetchAccountRequests = async () => {
    setLoadingAccountRequests(true);
    try {
      const data = await adminService?.getInstituteAccountRequests();
      setAccountRequests(data || []);
    } catch (error) {
      console.error('Error fetching account requests:', error);
    } finally {
      setLoadingAccountRequests(false);
    }
  };

  const handleUpdateRequestStatus = async (requestId, status) => {
    try {
      await adminService?.updateInstituteAccountRequestStatus(requestId, status);
      setAccountRequests(prev => prev?.map(r => r?.id === requestId ? { ...r, status } : r));
    } catch (error) {
      console.error('Error updating request status:', error);
      alert(error?.message || 'Failed to update status');
    }
  };

  const fetchUsers = async () => {
    setLoadingUsers(true);
    setUserError(null);
    try {
      const data = await adminService?.getAllUsers();
      setUsers(data);
    } catch (error) {
      console.error('Error fetching users:', error);
      setUserError(error?.message || 'Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchPendingInstitutes = async () => {
    setLoadingPendingInstitutes(true);
    setInstituteError(null);
    try {
      const data = await adminService?.getPendingInstitutes();
      setPendingInstitutes(data);
    } catch (error) {
      console.error('Error fetching pending institutes:', error);
      setInstituteError(error?.message || 'Failed to load pending institutes');
    } finally {
      setLoadingPendingInstitutes(false);
    }
  };

  const fetchAllInstitutes = async () => {
    setLoadingInstitutes(true);
    try {
      const data = await instituteService?.getAllInstitutesForAdmin();
      setInstitutes(data || []);
    } catch (error) {
      console.error('Error loading institutes:', error);
    } finally {
      setLoadingInstitutes(false);
    }
  };

  const fetchEventConfig = async () => {
    try {
      const config = await adminService?.getEventConfig();
      setEventConfig(config);
    } catch (error) {
      console.error('Error fetching event config:', error);
    }
  };

  const handleSaveEventConfig = async (config) => {
    setSavingConfig(true);
    try {
      await adminService?.saveEventConfig(config);
      setEventConfig(config);
      alert('Event configuration saved successfully');
    } catch (error) {
      console.error('Error saving event config:', error);
      alert(error?.message || 'Failed to save event configuration');
    } finally {
      setSavingConfig(false);
    }
  };

  const handleBulkUploadSuccess = (results) => {
    console.log('Bulk upload completed:', results);
    // Refresh users list
    fetchUsers();
  };

  const handleBulkInstituteUploadSuccess = (results) => {
    console.log('Bulk institute upload completed:', results);
    loadInstitutes();
    fetchAllInstitutes();
  };

  const handleDeleteInstitute = async () => {
    if (!deleteConfirmInstitute) return;
    setIsDeletingInstitute(true);
    try {
      await instituteService?.deleteInstitute(deleteConfirmInstitute?.id);
      setInstitutes(prev => prev?.filter(i => i?.id !== deleteConfirmInstitute?.id));
      setDeleteConfirmInstitute(null);
    } catch (error) {
      console.error('Error deleting institute:', error);
      alert(error?.message || 'Failed to delete institute. Please try again.');
    } finally {
      setIsDeletingInstitute(false);
    }
  };

  const handleDownloadBulkCredentials = () => {
    if (!institutes?.length) {
      alert('No institutes available to export.');
      return;
    }

    const rows = [
      ['Institute Name', 'Login Email', 'Password', 'Login URL', 'Status', 'Contact Person']
    ];

    institutes?.forEach((inst) => {
      rows?.push([
        inst?.instituteName || '',
        inst?.user?.email || '',
        inst?.user?.plain_password || 'N/A (not stored)',
        'https://studyexpo.online/institute-login',
        inst?.approvalStatus || '',
        inst?.contactPerson || ''
      ]);
    });

    const csvContent = rows?.map((row) =>
        row?.map((cell) => `"${String(cell)?.replace(/"/g, '""')}"`)?.join(',')
      )?.join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link?.setAttribute('download', `institute_credentials_${new Date()?.toISOString()?.slice(0, 10)}.csv`);
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const downloadInstitutesExcel = () => {
    const filtered = instituteSearch
      ? institutes?.filter(inst => {
          const q = instituteSearch?.toLowerCase();
          const name = (inst?.instituteName || '')?.toLowerCase();
          const city = (inst?.user?.city || '')?.toLowerCase();
          const state = (inst?.user?.state || '')?.toLowerCase();
          return name?.includes(q) || city?.includes(q) || state?.includes(q);
        })
      : institutes;

    const rows = [
      ['Institute Name', 'City'],
      ...(filtered || [])?.map(inst => [
        inst?.instituteName || '',
        inst?.user?.city || ''
      ])
    ];

    const csvContent = rows?.map(row => row?.map(cell => `"${String(cell)?.replace(/"/g, '""')}"`)?.join(','))?.join('\r\n');

    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'institutes.csv';
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-background">
      <AdminNavigation />
      <div className="pt-[60px]">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-6 md:mb-8">
            <div>
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-2">
                Admin Dashboard
              </h1>
              <p className="text-muted-foreground">
                Manage platform operations and monitor performance
              </p>
            </div>
            <div className="flex items-center gap-3">
              <NotificationPanel userId={user?.id} />

              <Button 
                variant="default" 
                iconName="Download"
                onClick={() => handleGenerateReport({ type: 'registrations', format: 'csv' })}
              >
                Export Data
              </Button>
            </div>
          </div>

          <div className="mb-6">
            <div className="flex flex-wrap gap-1 p-1 bg-muted/30 rounded-xl border border-border">
              {tabs?.map((tab) =>
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs transition-smooth ${
                activeTab === tab?.id ?
                'bg-white shadow-sm text-primary font-semibold border border-primary/20' : 'text-muted-foreground hover:text-foreground hover:bg-white/60 font-medium'}`
                }>
                  <Icon name={tab?.icon} size={13} />
                  <span>{tab?.label}</span>
                </button>
              )}
            </div>
          </div>

          {activeTab === 'overview' &&
          <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                {metrics?.map((metric, index) =>
              <MetricCard key={index} {...metric} />
              )}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <AnalyticsChart
                type="line"
                data={registrationData}
                title="Registration Trend"
                dataKey="value"
                xAxisKey="name" />

                <AnalyticsChart
                type="pie"
                data={engagementData}
                title="Engagement Distribution"
                dataKey="value" />

              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2">
                  <ActivityMonitor activities={activities} />
                </div>
                <ReportGenerator onGenerate={handleGenerateReport} />
              </div>
            </div>
          }

          {activeTab === 'users' &&
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-foreground">User Management</h2>
                <p className="text-muted-foreground mt-1">Manage students, institutes, and user accounts</p>
              </div>
              <Button
                iconName="Upload"
                onClick={() => handleUserAction('bulk-upload', [])}
              >
                Bulk Upload Students
              </Button>
            </div>
            {loadingUsers ? (
              <div className="flex items-center justify-center py-12">
                <Icon name="Loader2" size={48} className="animate-spin" color="var(--color-primary)" />
              </div>
            ) : userError ? (
              <div className="bg-error/10 border border-error text-error rounded-lg p-4">
                <p className="font-medium">Error loading users</p>
                <p className="text-sm mt-1">{userError}</p>
                <Button onClick={fetchUsers} variant="outline" size="sm" className="mt-3">
                  Retry
                </Button>
              </div>
            ) : (
              <UserManagementTable users={users} onAction={handleUserAction} />
            )}
            </div>
          }

          {activeTab === 'institutes' &&
          <div className="space-y-6">
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-heading font-bold text-foreground">Pending Approvals</h2>
                  <div className="flex flex-wrap items-center gap-2 justify-end">
                    <Button 
                      variant="outline" 
                      iconName="Upload"
                      onClick={() => setIsBulkInstituteUploadModalOpen(true)}
                    >
                      Bulk Upload Institutes
                    </Button>
                    <Button
                      variant="outline"
                      iconName="HelpCircle"
                      onClick={() => setIsBulkFaqUploadOpen(true)}
                    >
                      Bulk Upload FAQs
                    </Button>
                    <Button
                      variant="outline"
                      iconName="FileText"
                      onClick={() => setIsBulkDescriptionUploadOpen(true)}
                    >
                      Bulk Upload Descriptions
                    </Button>
                    <Button
                      variant="outline"
                      iconName="Star"
                      onClick={() => setIsBulkTestimonialUploadOpen(true)}
                    >
                      Bulk Upload Testimonials
                    </Button>
                    <Button
                      variant="outline"
                      iconName="Image"
                      onClick={() => setIsBulkLogoUploadOpen(true)}
                    >
                      Bulk Upload Logos
                    </Button>
                    <Button
                      variant="outline"
                      iconName="Download"
                      onClick={handleDownloadBulkCredentials}
                    >
                      Download Credentials
                    </Button>
                    <Button
                      variant="outline"
                      iconName="Image"
                      onClick={() => setActiveTab('booth-images')}
                      className="bg-primary/5 border-primary/30 text-primary hover:bg-primary/10"
                    >
                      Upload Banner / Logo
                    </Button>
                    <Button 
                      variant="default" 
                      iconName="Plus"
                      onClick={() => setIsAddInstituteModalOpen(true)}
                    >
                      Add Institute
                    </Button>
                    <span className="px-3 py-1 bg-warning/10 text-warning text-sm font-medium rounded-full">
                      {pendingInstitutes?.length} Pending
                    </span>
                  </div>
                </div>

                {loadingPendingInstitutes ? (
                  <div className="flex items-center justify-center py-12">
                    <Icon name="Loader2" size={48} className="animate-spin" color="var(--color-primary)" />
                  </div>
                ) : instituteError ? (
                  <div className="bg-error/10 border border-error text-error rounded-lg p-4">
                    <p className="font-medium">Error loading pending institutes</p>
                    <p className="text-sm mt-1">{instituteError}</p>
                    <Button onClick={fetchPendingInstitutes} variant="outline" size="sm" className="mt-3">
                      Retry
                    </Button>
                  </div>
                ) : pendingInstitutes?.length === 0 ? (
                  <div className="bg-card border border-border rounded-lg p-8 text-center">
                    <Icon name="CheckCircle" size={48} color="var(--color-success)" className="mx-auto mb-3" />
                    <p className="text-muted-foreground">No pending approvals</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {pendingInstitutes?.map((institute) => (
                      <InstituteApprovalCard
                        key={institute?.id}
                        institute={institute}
                        onApprove={handleApproveInstitute}
                        onReject={handleRejectInstitute}
                        onViewDetails={handleViewInstituteDetails}
                      />
                    ))}
                  </div>
                )}
              </div>

              <div className="bg-white rounded-lg border border-border p-4 mt-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                  <h3 className="text-lg font-semibold text-foreground">
                    All Institutes ({institutes?.filter(inst => {
                      const q = instituteSearch?.toLowerCase();
                      if (!q) return true;
                      const name = (inst?.instituteName || '')?.toLowerCase();
                      const city = (inst?.user?.city || '')?.toLowerCase();
                      const state = (inst?.user?.state || '')?.toLowerCase();
                      return name?.includes(q) || city?.includes(q) || state?.includes(q);
                    })?.length ?? 0} / {institutes?.length ?? 0})
                  </h3>
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    <div className="relative w-full sm:w-72">
                      <span className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                        <Icon name="Search" size={16} color="var(--color-muted-foreground)" />
                      </span>
                      <input
                        type="text"
                        value={instituteSearch}
                        onChange={e => setInstituteSearch(e?.target?.value)}
                        placeholder="Search by name or location…"
                        className="w-full pl-9 pr-8 py-2 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                      />
                      {instituteSearch && (
                        <button
                          onClick={() => setInstituteSearch('')}
                          className="absolute inset-y-0 right-2 flex items-center text-muted-foreground hover:text-foreground"
                        >
                          <Icon name="X" size={14} />
                        </button>
                      )}
                    </div>
                    <Button
                      variant="outline"
                      iconName="Download"
                      iconPosition="left"
                      onClick={downloadInstitutesExcel}
                      className="whitespace-nowrap"
                    >
                      Download Excel
                    </Button>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-border">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Institute</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Contact</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Location</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Courses</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Status</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Booth Type</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Chat Access</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Video Counseling</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-foreground">360° Tour</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {(instituteSearch
                        ? institutes?.filter(inst => {
                            const q = instituteSearch?.toLowerCase();
                            const name = (inst?.instituteName || '')?.toLowerCase();
                            const city = (inst?.user?.city || '')?.toLowerCase();
                            const state = (inst?.user?.state || '')?.toLowerCase();
                            return name?.includes(q) || city?.includes(q) || state?.includes(q);
                          })
                        : institutes
                      )?.map((institute) =>
                      <tr key={institute?.id} className="hover:bg-gray-50 transition-smooth">
                              <td className="px-4 py-3">
                                <div className="font-medium text-foreground">{institute?.instituteName}</div>
                                <div className="text-sm text-muted-foreground">{institute?.user?.email}</div>
                              </td>
                              <td className="px-4 py-3 text-sm text-muted-foreground">
                                {institute?.contactPerson || '-'}
                              </td>
                              <td className="px-4 py-3 text-sm text-muted-foreground">
                                {institute?.user?.city && institute?.user?.state ?
                          `${institute?.user?.city}, ${institute?.user?.state}` :
                          '-'}
                              </td>
                              <td className="px-4 py-3 text-sm text-muted-foreground">
                                {institute?.coursesCount || 0}
                              </td>
                              <td className="px-4 py-3">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          institute?.approvalStatus === 'approved' ? 'bg-green-100 text-green-800' :
                          institute?.approvalStatus === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`
                          }>
                                  {institute?.approvalStatus}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-muted-foreground capitalize">
                                {institute?.boothType}
                              </td>
                              <td className="px-4 py-3">
                                <button
                                  onClick={() => handleToggleActivation(institute?.id, 'chat', institute?.chatEnabled)}
                                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                                    institute?.chatEnabled ? 'bg-success' : 'bg-gray-300'
                                  }`}
                                  title={institute?.chatEnabled ? 'Disable Chat' : 'Enable Chat'}
                                >
                                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                                    institute?.chatEnabled ? 'translate-x-6' : 'translate-x-1'
                                  }`} />
                                </button>
                                <span className={`ml-2 text-xs font-medium ${institute?.chatEnabled ? 'text-success' : 'text-muted-foreground'}`}>
                                  {institute?.chatEnabled ? 'ON' : 'OFF'}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <button
                                  onClick={() => handleToggleActivation(institute?.id, 'video', institute?.videoEnabled)}
                                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                                    institute?.videoEnabled ? 'bg-primary' : 'bg-gray-300'
                                  }`}
                                  title={institute?.videoEnabled ? 'Disable Video Counseling' : 'Enable Video Counseling'}
                                >
                                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                                    institute?.videoEnabled ? 'translate-x-6' : 'translate-x-1'
                                  }`} />
                                </button>
                                <span className={`ml-2 text-xs font-medium ${institute?.videoEnabled ? 'text-primary' : 'text-muted-foreground'}`}>
                                  {institute?.videoEnabled ? 'ON' : 'OFF'}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <button
                                  onClick={() => handleToggleActivation(institute?.id, 'tour360', institute?.tour360Enabled)}
                                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                                    institute?.tour360Enabled ? 'bg-secondary' : 'bg-gray-300'
                                  }`}
                                  title={institute?.tour360Enabled ? 'Disable 360° Tour' : 'Enable 360° Tour'}
                                >
                                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                                    institute?.tour360Enabled ? 'translate-x-6' : 'translate-x-1'
                                  }`} />
                                </button>
                                <span className={`ml-2 text-xs font-medium ${institute?.tour360Enabled ? 'text-secondary' : 'text-muted-foreground'}`}>
                                  {institute?.tour360Enabled ? 'ON' : 'OFF'}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => { setCredentialsInstitute(institute); setIsViewCredentialsModalOpen(true); }}
                                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-primary hover:bg-primary/90 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-primary/40"
                                    title="View Login Credentials"
                                  >
                                    <Icon name="KeyRound" size={12} />
                                    Credentials
                                  </button>
                                  <button
                                    onClick={() => handleEditInstitute(institute)}
                                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-secondary hover:bg-secondary/90 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-secondary/40"
                                    title="Edit Institute Profile"
                                  >
                                    <Icon name="Pencil" size={12} />
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => setDeleteConfirmInstitute(institute)}
                                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-red-500 hover:bg-red-600 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-red-400"
                                    title="Delete Institute"
                                  >
                                    <Icon name="Trash2" size={12} />
                                    Delete
                                  </button>
                                </div>
                              </td>
                            </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          }

          {activeTab === 'analytics' &&
          <div className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <AnalyticsChart
                type="bar"
                data={registrationData}
                title="Daily Registrations (Last 7 Days)"
                dataKey="value"
                xAxisKey="name" />

                <AnalyticsChart
                type="line"
                data={boothVisitsTrend}
                title="Booth Visit Traffic (Last 7 Days)"
                dataKey="value"
                xAxisKey="name" />

              </div>
              <AnalyticsChart
              type="pie"
              data={engagementData}
              title="Platform Engagement"
              dataKey="value" />

            </div>
          }

          {activeTab === 'config' &&
          <div className="space-y-6">
            {eventConfig ? (
              <EventConfigPanel 
                config={eventConfig} 
                onSave={handleSaveEventConfig}
                saving={savingConfig}
              />
            ) : (
              <div className="flex items-center justify-center py-12">
                <Icon name="Loader2" size={48} className="animate-spin" color="var(--color-primary)" />
              </div>
            )}
            </div>
          }

          {activeTab === 'reminders' &&
          <div className="space-y-6">
            <EventRemindersPanel />
          </div>
          }

          {activeTab === 'announcements' &&
          <div className="space-y-6">
            <AnnouncementManager />
          </div>
          }

          {activeTab === 'webinars' && <WebinarManagementPanel />}

          {activeTab === 'booth-images' &&
          <div className="space-y-6">
            <BoothImageManager />
          </div>
          }

          {activeTab === 'booths' &&
          <div className="space-y-6">
            <BoothManagementPanel />
          </div>
          }

          {activeTab === 'exhibition-hall' &&
          <div className="space-y-6">
            <ExhibitionHallManager />
          </div>
          }

          {activeTab === 'featured' &&
          <div className="space-y-6">
            <FeaturedInstitutesManager />
          </div>
          }

          {activeTab === 'categories' &&
          <div className="space-y-6">
            <CategoryEditor />
          </div>
          }

          {activeTab === 'course-library' &&
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h2 className="text-2xl font-heading font-bold text-foreground">Course Library</h2>
                <p className="text-muted-foreground text-sm mt-0.5">Manage global categories, levels, and bulk-upload courses for all institutes.</p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  iconName="FileSpreadsheet"
                  onClick={() => setIsBulkExcelCourseUploadOpen(true)}
                >
                  Bulk Upload (Excel)
                </Button>
                <Button
                  variant="default"
                  iconName="FileText"
                  onClick={() => setIsBulkCsvCourseUploadOpen(true)}
                >
                  Bulk Upload (CSV — Name+City)
                </Button>
              </div>
            </div>
            <CourseLibraryManager />
          </div>
          }

          {activeTab === 'student-approvals' && (
            <div className="space-y-6">
              <StudentApprovalPanel />
            </div>
          )}

          {activeTab === 'account-requests' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-foreground">Institute Account Requests</h2>
                  <p className="text-muted-foreground mt-1">Requests submitted via the "Request Institute Account" form</p>
                </div>
                <Button variant="outline" iconName="RefreshCw" onClick={fetchAccountRequests}>
                  Refresh
                </Button>
              </div>

              {loadingAccountRequests ? (
                <div className="flex items-center justify-center py-12">
                  <Icon name="Loader2" size={48} className="animate-spin" color="var(--color-primary)" />
                </div>
              ) : accountRequests?.length === 0 ? (
                <div className="bg-card border border-border rounded-lg p-12 text-center">
                  <Icon name="ClipboardList" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3 opacity-40" />
                  <p className="text-lg font-medium text-foreground mb-1">No requests yet</p>
                  <p className="text-sm text-muted-foreground">Institute account requests will appear here once submitted.</p>
                </div>
              ) : (
                <div className="bg-white rounded-lg border border-border overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-border">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Institute Name</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Contact Person</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Email</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Phone</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Message</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Submitted</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Status</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {accountRequests?.map((req) => (
                          <tr key={req?.id} className="hover:bg-gray-50 transition-smooth">
                            <td className="px-4 py-3">
                              <div className="font-medium text-foreground">{req?.institute_name}</div>
                            </td>
                            <td className="px-4 py-3 text-sm text-muted-foreground">{req?.contact_person}</td>
                            <td className="px-4 py-3 text-sm text-muted-foreground">
                              <a href={`mailto:${req?.email}`} className="text-primary hover:underline">{req?.email}</a>
                            </td>
                            <td className="px-4 py-3 text-sm text-muted-foreground">{req?.phone || '—'}</td>
                            <td className="px-4 py-3 text-sm text-muted-foreground max-w-[200px]">
                              <span className="line-clamp-2" title={req?.message}>{req?.message || '—'}</span>
                            </td>
                            <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">
                              {new Date(req?.created_at)?.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                            </td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                req?.status === 'approved' ? 'bg-green-100 text-green-800' :
                                req?.status === 'rejected'? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {req?.status === 'approved' ? 'Approved' : req?.status === 'rejected' ? 'Rejected' : 'Pending'}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                {req?.status !== 'approved' && (
                                  <button
                                    onClick={() => handleUpdateRequestStatus(req?.id, 'approved')}
                                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors"
                                  >
                                    <Icon name="Check" size={12} />
                                    Approve
                                  </button>
                                )}
                                {req?.status !== 'rejected' && (
                                  <button
                                    onClick={() => handleUpdateRequestStatus(req?.id, 'rejected')}
                                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-red-500 hover:bg-red-600 rounded-md transition-colors"
                                  >
                                    <Icon name="X" size={12} />
                                    Reject
                                  </button>
                                )}
                                {req?.status !== 'pending' && (
                                  <button
                                    onClick={() => handleUpdateRequestStatus(req?.id, 'pending')}
                                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-foreground bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                                  >
                                    Reset
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <AddInstituteModal
        isOpen={isAddInstituteModalOpen}
        onClose={() => setIsAddInstituteModalOpen(false)}
        onSubmit={handleAddInstitute}
      />
      <EditUserModal
        isOpen={isEditUserModalOpen}
        onClose={() => {
          setIsEditUserModalOpen(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
        onSave={handleUpdateUser}
      />
      <ViewUserDetailsModal
        isOpen={isViewUserModalOpen}
        onClose={() => setIsViewUserModalOpen(false)}
        user={viewingUser}
      />
      <ViewInstituteDetailsModal
        isOpen={isViewInstituteModalOpen}
        onClose={() => {
          setIsViewInstituteModalOpen(false);
          setViewingInstitute(null);
        }}
        institute={viewingInstitute}
      />
      <EditInstituteModal
        isOpen={isEditInstituteModalOpen}
        onClose={() => {
          setIsEditInstituteModalOpen(false);
          setEditingInstitute(null);
        }}
        institute={editingInstitute}
        onSave={handleSaveInstituteProfile}
      />
      <ViewCredentialsModal
        isOpen={isViewCredentialsModalOpen}
        onClose={() => { setIsViewCredentialsModalOpen(false); setCredentialsInstitute(null); }}
        institute={credentialsInstitute}
      />
      <BulkStudentUploadModal
        isOpen={isBulkUploadModalOpen}
        onClose={() => setIsBulkUploadModalOpen(false)}
        onSuccess={handleBulkUploadSuccess}
      />
      <BulkInstituteUploadModal
        isOpen={isBulkInstituteUploadModalOpen}
        onClose={() => setIsBulkInstituteUploadModalOpen(false)}
        onSuccess={handleBulkInstituteUploadSuccess}
      />
      <BulkExcelCourseUploadModal
        isOpen={isBulkExcelCourseUploadOpen}
        onClose={() => setIsBulkExcelCourseUploadOpen(false)}
        onSuccess={() => setIsBulkExcelCourseUploadOpen(false)}
      />
      <BulkCsvCourseUploadModal
        isOpen={isBulkCsvCourseUploadOpen}
        onClose={() => setIsBulkCsvCourseUploadOpen(false)}
        onSuccess={() => setIsBulkCsvCourseUploadOpen(false)}
      />
      <BulkFaqUploadModal
        isOpen={isBulkFaqUploadOpen}
        onClose={() => setIsBulkFaqUploadOpen(false)}
        onSuccess={() => setIsBulkFaqUploadOpen(false)}
      />
      <BulkDescriptionUploadModal
        isOpen={isBulkDescriptionUploadOpen}
        onClose={() => setIsBulkDescriptionUploadOpen(false)}
        onSuccess={() => setIsBulkDescriptionUploadOpen(false)}
      />
      <BulkTestimonialUploadModal
        isOpen={isBulkTestimonialUploadOpen}
        onClose={() => setIsBulkTestimonialUploadOpen(false)}
        onSuccess={() => setIsBulkTestimonialUploadOpen(false)}
      />
      <BulkLogoUploadModal
        isOpen={isBulkLogoUploadOpen}
        onClose={() => setIsBulkLogoUploadOpen(false)}
        onSuccess={() => setIsBulkLogoUploadOpen(false)}
      />
      <InstituteConfirmationDialog
        isOpen={confirmDialog?.isOpen}
        action={confirmDialog?.action}
        institute={confirmDialog?.institute}
        onConfirm={handleConfirmAction}
        onCancel={handleCancelConfirm}
        isLoading={isConfirmLoading}
      />
      {/* Delete Institute Confirmation Modal */}
      {deleteConfirmInstitute && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <Icon name="Trash2" size={20} className="text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">Delete Institute</h3>
                <p className="text-sm text-muted-foreground">This action cannot be undone</p>
              </div>
            </div>
            <p className="text-sm text-foreground mb-6">
              Are you sure you want to permanently delete{' '}
              <span className="font-semibold text-red-600">{deleteConfirmInstitute?.instituteName}</span>?
              This will remove the institute profile, all associated data, and the user account.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirmInstitute(null)}
                disabled={isDeletingInstitute}
                className="px-4 py-2 text-sm font-medium text-foreground bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteInstitute}
                disabled={isDeletingInstitute}
                className="px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {isDeletingInstitute ? (
                  <>
                    <Icon name="Loader2" size={14} className="animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Icon name="Trash2" size={14} />
                    Delete Institute
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

 };

export default AdminDashboard;